import {
  createAsyncThunk,
  createSelector,
  createSlice,
  nanoid,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  Channel,
  PricingTemplate,
  VolSource,
  PriceSpec,
  getGroupOrder,
  getActiveDefs,
  deriveGroupPricing,
  BucketFigures,
  GroupMarkup,
  bucketToGroup,
  industryShares,
  groupVolShares,
  absorbGroupWeights,
  groupWeightsFromBuckets,
  computeTxnShares,
  computeVolShares,
  computeScenario,
  BREAKDOWN_COLORS,
  CITY_COORDS,
  GROUP_PROFILES,
  MCC_DATA,
  MCC_NAMES,
  groupBucketLabels,
} from "./pricingData";
import {
  MonthPeriod,
  containsMonth,
  daysInMonth,
  effectiveMonths,
  monthSpan,
  monthStartDate,
  periodEndDate,
  projectableDays,
  projectionFactor,
  resolvePeriod,
} from "./periodMonths";
import {
  MAX_SAVED_SCENARIOS,
  SavedScenario,
  ScenarioMetrics,
  applyScenarioInputs,
  cohortKey,
  persistSavedScenarios,
  readSavedScenarios,
  scenarioInputs,
  volumeBasisKey,
} from "./savedScenarios";
import { getMerchantInsightsFromServer } from "./pricingToolApi";
import { getBusinessesFromServer } from "../businesses/businessesApi";
import { getPartnersFromServer } from "../partners/partnersApi";
import {
  MerchantInsightsData,
  MerchantLocation,
  MonthlyBreakdownEntry,
  MonthlyBucketData,
  BucketData,
} from "../../interfaces/merchantInsights.interface";
import { IBusinessFull } from "../../interfaces/businessFull.interface";
import { IPartner } from "../../interfaces/partner.interface";

// ── Types ──

export interface BizOption {
  key: string;
  label: string;
  sub: string;
}

export interface StoredBiz {
  id: string;
  displayName: string;
  city: string;
  mcc: string | null;
  template: PricingTemplate;
  country: string;
  industryGroup: string;
  // The merchant's own geocoded position, absent for a business the geocoding
  // job has not covered. Only ever read through resolveAreaCentre.
  lat?: number;
  lng?: number;
}

export interface PricingToolState {
  channel: Channel;
  source: VolSource;
  template: PricingTemplate;
  country: string;

  bizId: string | null;
  selectedBiz: StoredBiz | null;
  bizById: Record<string, StoredBiz>;

  areaCentre: "city" | "business" | "address";
  areaBizId: string | null;
  areaBizCity: string;
  areaAddress: {
    label: string;
    lat: number;
    lng: number;
    country?: string;
  } | null;
  city: string;
  radiusKm: number;
  industryGroup: string;
  mcc: string | null;
  // Narrows the area cohort to one reseller's merchants. Most merchants belong
  // to nobody, so an unfiltered area averages a partner's cut down towards zero
  // and the after-markup basis reports a blend of several contracts and a lot of
  // direct merchants — a figure that describes no real deal.
  partnerId: string | null;
  partnerName: string | null;
  // Which merchants the area cohort may contain, before any one partner is
  // picked. "direct" are the ones Klearly keeps the whole fee on, so they price
  // a deal with no partner in it; "partners" is the mirror, and also the only
  // way to tell a cohort that reports no partner cut apart from a cut that is
  // not being reported at all.
  cohort: PartnerCohort;

  // Merchants under this average monthly volume are left out of the cohort.
  // Zero means take whatever traded at all, which is what the server did before
  // the threshold existed and so is the default.
  minMonthlyVolume: number;
  // How many the server set aside at that threshold, for the panel to state.
  // Null when nothing has been fetched or the deployment does not report it.
  apiExcludedCount: number | null;

  manualTPV: number;
  manualTxns: number;
  // The typed mix, as raw weights per group rather than shares that must total
  // 100. Nothing here is rewritten when a neighbour is typed; the normalising
  // to 100 happens once, at read time, in selectActiveVolShares.
  manualVol: Record<string, number> | null;
  // The same mix broken into card buckets, so a template switch can put each
  // typed weight on the rows that still contain those cards. The boxes read
  // manualVol, which is this projected onto the template on screen.
  manualBuckets: Record<string, number> | null;
  // Which basis those weights are in. Merchants know their mix by transaction
  // count far more often than by volume, so "count" lets them type what they
  // know and have the volume shares derived through average ticket size.
  mixBasis: MixBasis;

  // Inclusive whole-month bounds ("YYYY-MM") of the period the volume is
  // averaged over, or null for the default trailing window. Always read through
  // resolvePeriod, never directly — a persisted pair can have gone stale.
  periodStart: string | null;
  periodEnd: string | null;

  // Only the prices someone has actually typed. Everything else is derived from
  // the response, so a change of filter restates the prices for the merchants
  // now in scope instead of leaving the last cohort's on screen.
  priceOverrides: Record<string, PriceSpec>;
  // Only the partner cuts someone has actually retyped, for asking what a
  // renegotiated contract would be worth. Everything else is the measured cut,
  // so a change of filter restates it rather than carrying one cohort's
  // contract onto the next. Not persisted, for the reason a price is not: a
  // reload is not an edit, and a hypothesis restored as a fact is worse than
  // one lost.
  markupOverrides: Record<string, Partial<GroupMarkup>>;
  // Which net the panel states: before either partner's cut, or after both.
  // Only meaningful for a cohort whose response reports markup.
  netBasis: NetBasis;
  // Pricings someone asked to keep, newest first, to put a proposal beside the
  // one it is meant to beat. Persisted under their own key — see savedScenarios.
  savedScenarios: SavedScenario[];
  // Selected month identifier (e.g. "2026-01") from the server breakdown, or
  // null to model the whole fetch window (monthly averages across all months).
  selMonth: string | null;

  apiTpv: number | null;
  apiTxns: number | null;
  // What Klearly actually earned, per month, after processing cost — measured,
  // not modelled. The baseline the proposal is compared against, so that the
  // "before" side is a fact rather than the list price we hope people are on.
  apiNet: number | null;
  // The same, with the reseller's and the ISV's cut taken off, and whether the
  // response stated those cuts at all. Absent is not zero: a deployment that
  // predates the fields has to leave the panel on the before-markup basis
  // rather than report a partner merchant as keeping the partner's share.
  apiFinalNet: number | null;
  apiReportsMarkup: boolean;
  // Whole-period per-bucket gross volume & transaction counts from the server.
  // Drives the pricing mix (vol/txn shares) when no specific month is selected.
  apiBuckets: BucketAmounts | null;
  apiMonthly: MonthlyBreakdownEntry[] | null; // per-month metrics from server
  apiRange: { start: string; end: string } | null; // fetch window (YYYY-MM-DD)
  areaBusinesses: MerchantLocation[];
  fetchStatus: "idle" | "loading" | "succeeded" | "failed";

  bizSearchResults: BizOption[];
  bizSearchStatus: "idle" | "loading";
  areaBizSearchResults: BizOption[];
  areaBizSearchStatus: "idle" | "loading";
  partnerSearchResults: BizOption[];
  partnerSearchStatus: "idle" | "loading";
}

// ── Helpers ──

// Uniform per-bucket shape used to derive the pricing mix, regardless of whether
// it came from the whole-period averages or a single month's breakdown.
export type BucketAmounts = Record<string, BucketFigures>;

// Which of the two nets the panel reports. "after" takes the reseller's and the
// ISV's cut off, which is what Klearly actually keeps on a partner merchant.
export type NetBasis = "before" | "after";

// Which merchants an area cohort may contain at all. Three mutually exclusive
// states held as three states: two booleans would have four combinations, one
// of which asks for merchants that both have and have not a partner, and which
// the endpoint rejects outright.
export type PartnerCohort = "all" | "partners" | "direct";

// What a typed mix weight means. Percentage prices earn on volume and fixed
// prices earn per transaction, so a mix is only unambiguous once its basis is
// stated — and the two are not interchangeable: 50% of international
// transactions is about 68% of international volume.
export type MixBasis = "volume" | "count";

// Scale a month-in-progress's takings up to the full-month rate they are on
// course for. An unscaled partial month is the skew the whole-months rule exists
// to remove; scaling it is what makes including the current month honest rather
// than a reintroduction of that bug.
function projectEntry(
  entry: MonthlyBreakdownEntry,
  factor: number,
  daysCounted: number
): MonthlyBreakdownEntry {
  const scale = (b: MonthlyBucketData): MonthlyBucketData => ({
    avgGrossAmount: b.avgGrossAmount * factor,
    avgGrossRevenue: b.avgGrossRevenue * factor,
    avgProcessingCost: b.avgProcessingCost * factor,
    ...(b.avgPartnerMarkup == null
      ? {}
      : { avgPartnerMarkup: b.avgPartnerMarkup * factor }),
    ...(b.avgPartnerSVMarkup == null
      ? {}
      : { avgPartnerSVMarkup: b.avgPartnerSVMarkup * factor }),
    avgNetRevenue: b.avgNetRevenue * factor,
    avgFinalNetRevenue: b.avgFinalNetRevenue * factor,
    avgTransactionCount: b.avgTransactionCount * factor,
  });
  return {
    ...entry,
    avgTpv: entry.avgTpv * factor,
    avgTransactionCount: entry.avgTransactionCount * factor,
    avgNetRevenue: entry.avgNetRevenue * factor,
    avgFinalNetRevenue: entry.avgFinalNetRevenue * factor,
    // businessCount counts merchants, not money, so it is left alone.
    buckets: entry.buckets
      ? Object.fromEntries(
          Object.entries(entry.buckets).map(([k, v]) => [k, scale(v)])
        )
      : undefined,
    projected: { daysCounted, daysInMonth: daysInMonth(entry.month) },
  };
}

// Keep the months the chosen period actually asked for, in order, projecting
// the one still in progress. Selecting by period rather than by count is what
// stops an over-long window from being quietly answered as its most recent 12
// months.
export function monthsInPeriod(
  list: MonthlyBreakdownEntry[] | undefined,
  period: MonthPeriod,
  today?: Date
): MonthlyBreakdownEntry[] | null {
  if (!list || list.length === 0) return null;
  // Resolved once: asking the clock per month could straddle midnight and
  // project two months against two different notions of today.
  const now = today ?? new Date();
  const days = projectableDays(now);
  const inPeriod = list
    .filter((m) => containsMonth(period, m.month))
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((m) => {
      const factor = projectionFactor(m.month, now);
      return factor == null ? m : projectEntry(m, factor, days);
    });
  return inPeriod.length > 0 ? inPeriod : null;
}

export interface PeriodAverages {
  tpv: number | null;
  txns: number | null;
  net: number | null;
  // The same net with both partners' cuts taken off. The measured baseline for
  // the after-markup basis, so that basis opens on a fact too.
  finalNet: number | null;
  buckets: BucketAmounts | null;
  // Whether the response states a partner cut at all, as against stating zero.
  reportsMarkup: boolean;
}

// The headline monthly averages, derived from the very month bars they are
// drawn beside.
//
// `averages` from the endpoint divides each merchant's period total by the
// months the window touches, so a merchant that only traded two of them is
// counted as one doing a sixth of its real volume. The bars have no such
// problem: each divides by the merchants that actually traded that month.
// Averaging the bars therefore answers the question this tool is for — what a
// merchant around here turns over in a month it is open — and makes it
// impossible for the headline and the chart to state two volumes for one
// cohort, which they currently do by around 30%.
//
// Every month is already a full-month rate by the time it arrives, since
// `monthsInPeriod` projects the one still in progress, so a plain mean needs no
// month-count correction of its own.
export function periodAverages(
  result: MerchantInsightsData | undefined,
  period: MonthPeriod,
  today?: Date
): PeriodAverages {
  const months = monthsInPeriod(result?.monthlyBreakdown, period, today);
  if (!months) return rescaledAverages(result, period, today);

  const mean = (pick: (m: MonthlyBreakdownEntry) => number) =>
    months.reduce((sum, m) => sum + pick(m), 0) / months.length;

  return {
    tpv: mean((m) => m.avgTpv),
    txns: mean((m) => m.avgTransactionCount),
    // Net of processing cost but before partner markup, which is how the
    // modelled scenario computes its own net take. `avgFinalNetRevenue`
    // subtracts the partner's share too and would not be comparable.
    net: mean((m) => m.avgNetRevenue),
    // Net after both partners' cuts. Entry-level, where the pipeline does write
    // it — a bucket's own `avgFinalNetRevenue` is a default.
    finalNet: mean((m) => m.avgFinalNetRevenue),
    reportsMarkup: reportsMarkup(result),
    // Same months as the total they have to add up to, or the mix would be
    // priced against volume it does not describe.
    buckets:
      meanBuckets(months) ?? rescaledAverages(result, period, today).buckets,
  };
}

// Per-bucket means over the period's months.
function meanBuckets(months: MonthlyBreakdownEntry[]): BucketAmounts | null {
  const carrying = months.filter((m) => m.buckets);
  if (carrying.length === 0) return null;
  const out: BucketAmounts = {};
  carrying.forEach((m) =>
    Object.entries(m.buckets!).forEach(([k, v]) => {
      if (!out[k])
        out[k] = {
          gross: 0,
          txns: 0,
          revenue: 0,
          cost: 0,
          resellerMarkup: 0,
          isvMarkup: 0,
        };
      out[k].gross += v.avgGrossAmount;
      out[k].txns += v.avgTransactionCount;
      out[k].revenue += v.avgGrossRevenue;
      out[k].cost += v.avgProcessingCost;
      out[k].resellerMarkup += v.avgPartnerMarkup ?? 0;
      out[k].isvMarkup += v.avgPartnerSVMarkup ?? 0;
    })
  );
  Object.values(out).forEach((v) => {
    v.gross /= carrying.length;
    v.txns /= carrying.length;
    v.revenue /= carrying.length;
    v.cost /= carrying.length;
    v.resellerMarkup /= carrying.length;
    v.isvMarkup /= carrying.length;
  });
  return out;
}

// The fallback for a response with no monthly breakdown to average: the server's
// own figures, corrected for a window that stops part-way through its final
// month.
//
// The server divides period totals by the calendar months its window touches,
// which is the right divisor only when all of them are complete: eleven whole
// months plus a week gets divided by twelve. Rescaling by span / effectiveMonths
// fixes exactly that and nothing else, so the merchant-count denominator stays
// as the server computed it.
export function rescaledAverages(
  result: MerchantInsightsData | undefined,
  period: MonthPeriod,
  today?: Date
): PeriodAverages {
  const tpv = result?.averages?.monthlyTpv ?? null;
  const txns = result?.averages?.monthlyTransactionCount ?? null;
  const net = result?.averages?.monthlyNetRevenue ?? null;
  const finalNet = result?.averages?.monthlyFinalNetRevenue ?? null;
  const statesMarkup = reportsMarkup(result);
  const buckets = bucketsFromAverages(result?.averages?.buckets);
  const scale = monthSpan(period) / effectiveMonths(period, today);
  if (scale === 1)
    return { tpv, txns, net, finalNet, buckets, reportsMarkup: statesMarkup };
  return {
    tpv: tpv == null ? null : tpv * scale,
    txns: txns == null ? null : txns * scale,
    net: net == null ? null : net * scale,
    finalNet: finalNet == null ? null : finalNet * scale,
    reportsMarkup: statesMarkup,
    // Shares are unaffected by a scalar, but the absolute per-bucket amounts
    // are read directly for the mix's own totals.
    buckets: buckets
      ? Object.fromEntries(
          Object.entries(buckets).map(([k, v]) => [
            k,
            {
              gross: v.gross * scale,
              txns: v.txns * scale,
              revenue: v.revenue * scale,
              cost: v.cost * scale,
              resellerMarkup: v.resellerMarkup * scale,
              isvMarkup: v.isvMarkup * scale,
            },
          ])
        )
      : null,
  };
}

// Whole-period averages → uniform bucket amounts.
//
// Cost comes from `monthlyProcessingCost` and not from revenue less net, which
// looks like the same number and is not: `netRevenue` is not among the fields
// the analytics pipeline writes per bucket, so it reads back as 0 and the
// subtraction returns the whole revenue as cost — leaving every group with no
// margin and the panel showing a flat zero.
function bucketsFromAverages(
  buckets?: Record<string, BucketData>
): BucketAmounts | null {
  if (!buckets) return null;
  const out: BucketAmounts = {};
  Object.entries(buckets).forEach(([k, v]) => {
    out[k] = {
      gross: v.monthlyGrossAmount,
      txns: v.monthlyTransactionCount,
      revenue: v.monthlyGrossRevenue,
      cost: v.monthlyProcessingCost,
      resellerMarkup: v.monthlyPartnerMarkup ?? 0,
      isvMarkup: v.monthlyPartnerSVMarkup ?? 0,
    };
  });
  return out;
}

// Whether the response states a partner cut at all. A deployment that predates
// the fields reports nothing, which must not be read as "no markup" — that is
// the reading that would quietly overstate what Klearly keeps, and it is the
// whole reason this is tracked apart from the amount.
function reportsMarkup(result: MerchantInsightsData | undefined): boolean {
  const states = (b?: { avgPartnerMarkup?: number } | null) =>
    b?.avgPartnerMarkup != null;
  if (
    (result?.monthlyBreakdown ?? []).some((m) =>
      Object.values(m.buckets ?? {}).some(states)
    )
  )
    return true;
  return Object.values(result?.averages?.buckets ?? {}).some(
    (b) => b?.monthlyPartnerMarkup != null
  );
}

// A single month's breakdown → uniform bucket amounts.
function bucketsFromMonth(
  buckets?: Record<string, MonthlyBucketData>
): BucketAmounts | null {
  if (!buckets) return null;
  const out: BucketAmounts = {};
  Object.entries(buckets).forEach(([k, v]) => {
    out[k] = {
      gross: v.avgGrossAmount,
      txns: v.avgTransactionCount,
      revenue: v.avgGrossRevenue,
      cost: v.avgProcessingCost,
      resellerMarkup: v.avgPartnerMarkup ?? 0,
      isvMarkup: v.avgPartnerSVMarkup ?? 0,
    };
  });
  return out;
}

// Normalize per-bucket amounts (gross volume or txn count) into group-level
// percentage shares. Returns null when there's no activity to share out.
function bucketsToGroupShares(
  buckets: BucketAmounts,
  order: string[],
  b2g: Record<string, string>,
  key: "gross" | "txns"
): Record<string, number> | null {
  const total = Object.values(buckets).reduce((a, v) => a + (v[key] || 0), 0);
  if (total <= 0) return null;
  const normalized: Record<string, number> = {};
  Object.entries(buckets).forEach(
    ([k, v]) => (normalized[k] = ((v[key] || 0) / total) * 100)
  );
  return groupVolShares(normalized, order, b2g);
}

// `[lng, lat]`, per GeoJSON.
function bizCoords(b: IBusinessFull): { lat?: number; lng?: number } {
  const c = b.location?.coordinates;
  return c ? { lat: c[1], lng: c[0] } : {};
}

function toStoredBiz(b: IBusinessFull): StoredBiz {
  const mcc = b.merchantCategoryCode ? String(b.merchantCategoryCode) : null;
  const names = (b.merchantServiceChargeFees || []).map((f) => f.name);
  const template: PricingTemplate = names.some((n) => n === "European Debit")
    ? "europeanDebitSplit"
    : names.some((n) => n === "Domestic Credit")
      ? "domesticCreditSplit"
      : "default";
  return {
    id: b._id,
    displayName: b.businessTradeName || b.businessName || "Unknown",
    city: b.address?.city || "",
    mcc,
    template,
    country: b.address?.country || "NL",
    industryGroup: (mcc && MCC_DATA[mcc]) || "Other",
    ...bizCoords(b),
  };
}

// The coordinates the area query is centred on, or null when the chosen centre
// has none. Null has to stay distinguishable from a default: centring a miss on
// Amsterdam silently reports Dutch volume for an area the user never asked
// about, which is worse than reporting nothing.
export function resolveAreaCentre(
  s: PricingToolState
): { lat: number; lng: number } | null {
  if (s.areaCentre === "business") {
    const biz = s.areaBizId ? s.bizById[s.areaBizId] : null;
    if (biz?.lat != null && biz?.lng != null) {
      return { lat: biz.lat, lng: biz.lng };
    }
    // Not yet geocoded. CITY_COORDS only covers six Dutch cities, so this
    // rescues a Dutch merchant and resolves to nothing for anyone else.
    const byCity = biz?.city ? CITY_COORDS[biz.city] : undefined;
    return byCity ? { lat: byCity.lat, lng: byCity.lng } : null;
  }
  if (s.areaCentre === "address") {
    return s.areaAddress
      ? { lat: s.areaAddress.lat, lng: s.areaAddress.lng }
      : null;
  }
  const byCity = CITY_COORDS[s.city];
  return byCity ? { lat: byCity.lat, lng: byCity.lng } : null;
}

// Adopt the country of whatever the area is centred on. The insights query ANDs
// `address.country` with the geo radius, so a stale country silently empties
// every result outside it; it also selects the domestic-debit scheme the
// pricing defs are built from. Codes the defs don't know fall back to NL
// pricing internally, which still beats querying the wrong country.
function adoptCountry(state: PricingToolState, country?: string | null): void {
  const code = country?.trim().toUpperCase();
  if (!code || code.length !== 2 || code === state.country) return;
  state.country = code;
  // The domestic-debit scheme changes with the country, and so does its unit, so
  // a price typed against the old one no longer describes anything.
  delete state.priceOverrides.domesticDebit;
}

function bizSearchResults(businesses: IBusinessFull[]): {
  options: BizOption[];
  stored: Record<string, StoredBiz>;
} {
  const stored: Record<string, StoredBiz> = {};
  const options: BizOption[] = businesses.map((b) => {
    const sb = toStoredBiz(b);
    stored[b._id] = sb;
    return {
      key: b._id,
      label: sb.displayName,
      sub: [sb.city, sb.mcc ? MCC_NAMES[sb.mcc] || "MCC " + sb.mcc : null]
        .filter(Boolean)
        .join(" · "),
    };
  });
  return { options, stored };
}

// ── Initial state ──

const baseInitialState: PricingToolState = {
  channel: "offline",
  source: "business",
  template: "default",
  country: "NL",

  bizId: null,
  selectedBiz: null,
  bizById: {},

  areaCentre: "address",
  areaBizId: null,
  areaBizCity: "Amsterdam",
  areaAddress: null,
  city: "Amsterdam",
  radiusKm: 5,
  industryGroup: "Restaurants & Bars",
  mcc: null,
  partnerId: null,
  partnerName: null,
  cohort: "all",

  manualTPV: 85000,
  manualTxns: 2500,
  minMonthlyVolume: 0,
  apiExcludedCount: null,
  manualVol: null,
  manualBuckets: null,
  mixBasis: "volume",

  periodStart: null,
  periodEnd: null,

  priceOverrides: {},
  markupOverrides: {},
  selMonth: null,

  apiTpv: null,
  apiTxns: null,
  apiNet: null,
  apiFinalNet: null,
  apiReportsMarkup: false,
  netBasis: "before" as NetBasis,
  savedScenarios: [],
  apiBuckets: null,
  apiMonthly: null,
  apiRange: null,
  areaBusinesses: [],
  fetchStatus: "idle",

  bizSearchResults: [],
  bizSearchStatus: "idle",
  areaBizSearchResults: [],
  areaBizSearchStatus: "idle",
  partnerSearchResults: [],
  partnerSearchStatus: "idle",
};

// ── Persistence (filters survive a page refresh) ──

const PERSIST_KEY = "pricingTool:filters:v1";

// Only the user-chosen filters/inputs are persisted. Volatile, server-derived
// data (search results, fetch status, fetched insights) is intentionally left out.
//
// Exported so a test can hold this list and SCENARIO_INPUT_KEYS in step: a
// filter the page restores for itself but a saved scenario does not carry would
// survive loading a scenario, leaving the figures described by one selection
// and modelled on another.
export const PERSISTED_KEYS = [
  "channel",
  "source",
  "template",
  "country",
  "bizId",
  "selectedBiz",
  "bizById",
  "areaCentre",
  "areaBizId",
  "areaBizCity",
  "areaAddress",
  "city",
  "radiusKm",
  "industryGroup",
  "mcc",
  "partnerId",
  "partnerName",
  "cohort",
  "minMonthlyVolume",
  "manualTPV",
  "manualTxns",
  "manualVol",
  "manualBuckets",
  "mixBasis",
  "netBasis",
  "periodStart",
  "periodEnd",
  "selMonth",
] as const;

// Bumped when a persisted field stops meaning what it used to. Still written so
// an older build reading a newer blob can tell the two apart.
const PERSIST_VERSION = 4;

export function loadPersistedState(
  raw: string | null
): Partial<PricingToolState> {
  try {
    if (!raw) return {};
    const parsed = JSON.parse(raw) as
      (Partial<PricingToolState> & { version?: number }) | null;
    if (!parsed || typeof parsed !== "object") return {};
    const { version: _version, ...stored } = parsed;
    // The selection survives a reload; a price never does. A load is not an
    // edit, and a price stored against another country's scheme describes
    // nothing here.
    delete (stored as Record<string, unknown>).proposed;
    delete stored.priceOverrides;
    delete stored.markupOverrides;
    // A blob from the build that shipped the direct-only checkbox says
    // `directOnly`. Read as the cohort it meant rather than dropped, so nobody
    // finds their filter silently off after an update.
    const legacy = stored as Record<string, unknown>;
    if (stored.cohort === undefined && legacy.directOnly !== undefined) {
      stored.cohort = legacy.directOnly ? "direct" : "all";
    }
    delete legacy.directOnly;
    return stored;
  } catch {
    return {};
  }
}

// The scenario list last written out. The subscriber runs on every change the
// slice makes, and a price slider makes one per frame, so the scenarios — which
// only change when someone saves or deletes one — are written on reference
// change rather than restringified twenty at a time behind each drag.
let lastPersistedScenarios: SavedScenario[] | null = null;

export function persistPricingToolState(state: PricingToolState): void {
  try {
    const toSave: Record<string, unknown> = { version: PERSIST_VERSION };
    PERSISTED_KEYS.forEach((k) => {
      toSave[k] = state[k];
    });
    window.localStorage.setItem(PERSIST_KEY, JSON.stringify(toSave));
  } catch {
    // ignore (e.g. storage full / unavailable)
  }
  if (
    state.savedScenarios !== lastPersistedScenarios &&
    persistSavedScenarios(state.savedScenarios)
  ) {
    lastPersistedScenarios = state.savedScenarios;
  }
}

function readPersistedState(): Partial<PricingToolState> {
  try {
    return loadPersistedState(window.localStorage.getItem(PERSIST_KEY));
  } catch {
    return {};
  }
}

const initialState: PricingToolState = {
  ...baseInitialState,
  ...readPersistedState(),
  savedScenarios: readSavedScenarios(),
};

// Already on disk, so the first subscriber call has nothing to write back.
lastPersistedScenarios = initialState.savedScenarios;

// ── Thunks ──

export const fetchInsights = createAsyncThunk(
  "pricingTool/fetchInsights",
  async (_, { getState, rejectWithValue }) => {
    const s = (getState() as RootState).pricingTool;
    const {
      channel,
      source,
      bizId,
      country,
      city,
      industryGroup,
      mcc,
      radiusKm,
      partnerId,
      cohort,
      minMonthlyVolume,
    } = s;

    if (source === "manual") return rejectWithValue("manual");
    if (source === "business" && !bizId) return rejectWithValue("no-biz");

    // Whole calendar months, except a trailing month still in progress, which
    // is asked for up to its last complete day and projected on arrival. The
    // period is resolved rather than read so a stale stored one can't leak into
    // the query.
    const period = resolvePeriod(s.periodStart, s.periodEnd);
    const start = monthStartDate(period.start);
    const end = periodEndDate(period);

    if (source === "business") {
      const result = await getMerchantInsightsFromServer({
        start,
        end,
        businessIds: bizId!,
      });
      const monthly = monthsInPeriod(result?.monthlyBreakdown, period);
      return {
        // Per-bucket amounts drive the vol/txn-share mix for this merchant.
        ...periodAverages(result, period),
        monthly,
        range: { start, end },
        businesses: [] as MerchantLocation[],
        // One named merchant is not a cohort, so nothing was set aside.
        excludedCount: null as number | null,
      };
    }

    // area
    const centreCoords = resolveAreaCentre(s);
    if (!centreCoords) return rejectWithValue("no-area-centre");
    const params: Record<string, unknown> = {
      start,
      end,
      lat: centreCoords.lat,
      lng: centreCoords.lng,
      radiusKm,
      country,
    };
    if (mcc) params.mcc = mcc;
    else if (industryGroup) params.industryGroup = industryGroup;
    if (partnerId) params.partnerId = partnerId;
    else if (cohort === "direct") params.directOnly = true;
    else if (cohort === "partners") params.partnersOnly = true;
    // Omitted at zero rather than sent as 0, so a deployment that does not know
    // the param is given nothing to reject.
    if (minMonthlyVolume > 0) params.minMonthlyVolume = minMonthlyVolume;

    try {
      const result = await getMerchantInsightsFromServer(params as any);
      const monthly = monthsInPeriod(result?.monthlyBreakdown, period);
      return {
        // The area aggregate now also returns a real per-bucket mix.
        ...periodAverages(result, period),
        monthly,
        range: { start, end },
        businesses: (result?.businesses || []) as MerchantLocation[],
        excludedCount: result?.excludedBelowThreshold ?? null,
      };
    } catch {
      // Fallback to industry benchmark. Not for a partner cohort: the benchmark
      // describes an industry in a city, so presenting it as that reseller's
      // merchants would put a number on screen that nothing measured.
      if (partnerId || cohort !== "all")
        return rejectWithValue("partner-fetch-failed");
      const prof =
        GROUP_PROFILES[industryGroup] || GROUP_PROFILES["Restaurants & Bars"];
      const rf = Math.max(0.85, Math.min(1.25, 0.85 + radiusKm * 0.016));
      const cf = (CITY_COORDS[city] || { f: 1 }).f;
      const benchTpv =
        (channel === "online" ? prof.benchOn : prof.benchOff) * rf * cf;
      const atv = channel === "online" ? prof.atvOn : prof.atvOff;
      return {
        tpv: benchTpv,
        txns: atv > 0 ? benchTpv / atv : 0,
        // A benchmark is a guess at volume; nobody earned anything on it, so
        // there is no measured baseline to show against the proposal.
        net: null,
        finalNet: null,
        reportsMarkup: false,
        buckets: null,
        monthly: null,
        range: { start, end },
        businesses: [] as MerchantLocation[],
        // A benchmark is modelled, not measured, so no merchant was set aside.
        excludedCount: null as number | null,
      };
    }
  }
);

export const searchBusinessesThunk = createAsyncThunk(
  "pricingTool/searchBusinesses",
  async (query: string) => {
    const res = await getBusinessesFromServer({
      currentPage: 1,
      pageSize: 20,
      name: query,
    } as any);
    return bizSearchResults((res as any).data || []);
  }
);

export const searchAreaBusinessesThunk = createAsyncThunk(
  "pricingTool/searchAreaBusinesses",
  async (query: string) => {
    const res = await getBusinessesFromServer({
      currentPage: 1,
      pageSize: 20,
      name: query,
    } as any);
    const businesses: IBusinessFull[] = (res as any).data || [];
    const stored: Record<string, StoredBiz> = {};
    const options: BizOption[] = businesses.map((b) => {
      const sb = toStoredBiz(b);
      stored[b._id] = sb;
      return { key: b._id, label: sb.displayName, sub: sb.city };
    });
    return { options, stored };
  }
);

export const searchPartnersThunk = createAsyncThunk(
  "pricingTool/searchPartners",
  async (query: string) => {
    const res = await getPartnersFromServer({
      currentPage: 1,
      pageSize: 20,
      ...(query.trim() ? { name: query.trim() } : {}),
    });
    return ((res?.data ?? []) as IPartner[]).map((p) => ({
      key: p._id,
      label: p.name,
      sub: p.partnerType,
    }));
  }
);

function clearManualMix(state: PricingToolState): void {
  state.manualVol = null;
  state.manualBuckets = null;
}

// Keep a typed mix when the template changes. Rows whose cards did not move
// keep the weight that was typed. A row that is split shares that weight
// between the rows its cards moved to, and switching back restores it,
// because the card weights — not the rows — are what is remembered.
function retargetMix(state: PricingToolState, next: PricingTemplate): void {
  if (next !== state.template && state.manualVol) {
    const industry = industryShares(state.industryGroup, state.channel);
    const buckets =
      state.manualBuckets ??
      absorbGroupWeights(
        state.manualVol,
        bucketToGroup(state.channel, state.template),
        industry,
        null
      );
    state.manualBuckets = buckets;
    state.manualVol = groupWeightsFromBuckets(
      buckets,
      getGroupOrder(state.channel, next),
      bucketToGroup(state.channel, next)
    );
  }
  state.template = next;
}

// ── Slice ──

const pricingToolSlice = createSlice({
  name: "pricingTool",
  initialState,
  reducers: {
    setChannel(state, action: PayloadAction<Channel>) {
      state.channel = action.payload;
      clearManualMix(state);
    },
    setSource(state, action: PayloadAction<VolSource>) {
      state.source = action.payload;
      clearManualMix(state);
      // A cut typed against one volume source describes that source. Carried
      // onto the next one it would price a merchant with somebody else's terms.
      state.markupOverrides = {};
      state.apiTpv = null;
      state.apiTxns = null;
      state.apiNet = null;
      state.apiFinalNet = null;
      state.apiReportsMarkup = false;
      state.apiBuckets = null;
      state.apiMonthly = null;
      state.apiRange = null;
      state.selMonth = null;
    },
    setTemplate(state, action: PayloadAction<PricingTemplate>) {
      retargetMix(state, action.payload);
    },
    setCountry(state, action: PayloadAction<string>) {
      state.country = action.payload;
      delete state.priceOverrides.domesticDebit;
    },
    selectBiz(state, action: PayloadAction<string>) {
      const biz = state.bizById[action.payload];
      if (!biz) return;
      state.bizId = action.payload;
      state.selectedBiz = biz;
      state.industryGroup = biz.industryGroup;
      state.mcc = biz.mcc;
      if (state.channel === "offline") retargetMix(state, biz.template);
      adoptCountry(state, biz.country);
      state.apiTpv = null;
      state.apiTxns = null;
      state.apiNet = null;
      state.apiFinalNet = null;
      state.apiReportsMarkup = false;
      state.apiBuckets = null;
      state.apiMonthly = null;
      state.apiRange = null;
      state.selMonth = null;
    },
    setAreaCentre(
      state,
      action: PayloadAction<"city" | "business" | "address">
    ) {
      state.areaCentre = action.payload;
    },
    selectAreaBiz(state, action: PayloadAction<string>) {
      const biz = state.bizById[action.payload];
      state.areaBizId = action.payload;
      if (biz) {
        state.areaBizCity = biz.city || "Amsterdam";
        adoptCountry(state, biz.country);
      }
    },
    setAreaAddress(
      state,
      action: PayloadAction<{
        label: string;
        lat: number;
        lng: number;
        country?: string;
      } | null>
    ) {
      state.areaAddress = action.payload;
      adoptCountry(state, action.payload?.country);
    },
    setCity(state, action: PayloadAction<string>) {
      state.city = action.payload;
    },
    setRadiusKm(state, action: PayloadAction<number>) {
      state.radiusKm = action.payload;
    },
    setIndustryGroup(state, action: PayloadAction<string>) {
      state.industryGroup = action.payload;
      state.mcc = null;
    },
    // The name travels with the id so the scope chip can say who was picked
    // without a second read, and still says it after a reload.
    setPartner(
      state,
      action: PayloadAction<{ id: string; name: string } | null>
    ) {
      state.partnerId = action.payload?.id ?? null;
      state.partnerName = action.payload?.name ?? null;
      if (action.payload && state.cohort === "direct") state.cohort = "all";
      state.markupOverrides = {};
      state.apiTpv = null;
      state.apiTxns = null;
      state.apiNet = null;
      state.apiFinalNet = null;
      state.apiReportsMarkup = false;
      state.apiBuckets = null;
      state.apiMonthly = null;
      state.apiRange = null;
      state.areaBusinesses = [];
      state.selMonth = null;
    },
    setCohort(state, action: PayloadAction<PartnerCohort>) {
      state.cohort = action.payload;
      // A named partner cannot survive a move to direct-only, which excludes
      // every partner. It survives a move to partners-only, which is the wider
      // version of the same question.
      if (action.payload === "direct") {
        state.partnerId = null;
        state.partnerName = null;
      }
      state.markupOverrides = {};
      state.apiTpv = null;
      state.apiTxns = null;
      state.apiNet = null;
      state.apiFinalNet = null;
      state.apiReportsMarkup = false;
      state.apiBuckets = null;
      state.apiMonthly = null;
      state.apiRange = null;
      state.areaBusinesses = [];
      state.selMonth = null;
    },
    setMcc(
      state,
      action: PayloadAction<{ mcc: string; industryGroup: string }>
    ) {
      state.mcc = action.payload.mcc;
      state.industryGroup = action.payload.industryGroup;
    },
    setManualTPV(state, action: PayloadAction<number>) {
      state.manualTPV = Math.max(0, action.payload);
    },
    setManualTxns(state, action: PayloadAction<number>) {
      state.manualTxns = Math.max(1, action.payload);
    },
    // Only two of volume, count and average ticket are independent, and volume
    // and count are the pair that is stored. Typing a ticket therefore holds
    // the count and moves the volume: the count is the figure a merchant is
    // surest of, so it is the one an estimate should not quietly overwrite.
    setManualTicket(state, action: PayloadAction<number>) {
      const ticket = Math.max(0, action.payload);
      state.manualTPV = state.manualTxns * ticket;
    },
    // Changing the floor changes who is in the cohort, so every measured figure
    // is dropped: holding them would caption one cohort's volume with another's
    // merchant count until the refetch landed.
    setMinMonthlyVolume(state, action: PayloadAction<number>) {
      state.minMonthlyVolume = Math.max(0, action.payload);
      state.apiTpv = null;
      state.apiTxns = null;
      state.apiNet = null;
      state.apiFinalNet = null;
      state.apiReportsMarkup = false;
      state.apiBuckets = null;
      state.apiMonthly = null;
      state.apiRange = null;
      state.apiExcludedCount = null;
      state.areaBusinesses = [];
      state.selMonth = null;
    },
    setManualVol(state, action: PayloadAction<Record<string, number> | null>) {
      state.manualVol = action.payload;
      if (!action.payload) {
        state.manualBuckets = null;
        return;
      }
      state.manualBuckets = absorbGroupWeights(
        action.payload,
        bucketToGroup(state.channel, state.template),
        industryShares(state.industryGroup, state.channel),
        state.manualBuckets
      );
    },
    // Changing basis drops whatever was typed. The same numbers mean a
    // different mix in the other basis, so carrying them over would quietly
    // restate the user's input as something they never said; the mix falls back
    // to what was measured and is retyped in the new basis.
    setMixBasis(state, action: PayloadAction<MixBasis>) {
      if (state.mixBasis === action.payload) return;
      state.mixBasis = action.payload;
      clearManualMix(state);
    },
    setProposedValue(
      state,
      action: PayloadAction<{
        key: string;
        value: number;
        valueType: "Percentage" | "Fixed";
      }>
    ) {
      const { key, value, valueType } = action.payload;
      // The unit travels with the edit: there may be no override yet to take it
      // from, and the derived price's unit is the one on screen.
      state.priceOverrides[key] = { value, valueType };
    },
    setProposedType(
      state,
      action: PayloadAction<{
        key: string;
        valueType: "Percentage" | "Fixed";
        value: number;
      }>
    ) {
      const { key, valueType, value } = action.payload;
      state.priceOverrides[key] = { value, valueType };
    },
    // Retyping a partner's cut. The merchant keeps paying the same total, so
    // what moves is Klearly's share of it — the opposite of retyping the price.
    setMarkupValue(
      state,
      action: PayloadAction<{
        key: string;
        part: "reseller" | "isv";
        value: number;
        valueType: "Percentage" | "Fixed";
      }>
    ) {
      const { key, part, value, valueType } = action.payload;
      const current = state.markupOverrides[key] ?? {};
      state.markupOverrides[key] = {
        ...current,
        [part]: { value: Math.max(0, value), valueType },
      };
    },
    clearMarkupOverrides(state) {
      state.markupOverrides = {};
    },
    setNetBasis(state, action: PayloadAction<NetBasis>) {
      state.netBasis = action.payload;
    },
    setSelMonth(state, action: PayloadAction<string | null>) {
      state.selMonth = action.payload;
    },
    // null restores the default trailing window. Callers must have validated
    // the span; an invalid pair would simply be resolved away on read.
    setPeriod(state, action: PayloadAction<MonthPeriod | null>) {
      state.periodStart = action.payload?.start ?? null;
      state.periodEnd = action.payload?.end ?? null;
      // A month picked out of the old period must not go on being modelled
      // under the new one.
      const period = resolvePeriod(state.periodStart, state.periodEnd);
      if (state.selMonth && !containsMonth(period, state.selMonth)) {
        state.selMonth = null;
      }
      // Everything fetched describes the previous period.
      state.apiTpv = null;
      state.apiTxns = null;
      state.apiNet = null;
      state.apiFinalNet = null;
      state.apiReportsMarkup = false;
      state.apiBuckets = null;
      state.apiMonthly = null;
      state.apiRange = null;
    },
    // Keep the pricing on screen under a name, with the figures it produced, so
    // the next template can be tried without losing what this one came to.
    saveScenario: {
      reducer(
        state,
        action: PayloadAction<{
          id: string;
          name: string;
          savedAt: string;
          metrics: ScenarioMetrics;
        }>
      ) {
        const { id, name, savedAt, metrics } = action.payload;
        state.savedScenarios = [
          {
            id,
            name: name.trim() || "Untitled pricing",
            savedAt,
            metrics,
            inputs: scenarioInputs(state),
          },
          ...state.savedScenarios,
        ].slice(0, MAX_SAVED_SCENARIOS);
      },
      // The id and the clock belong to the dispatch, not to the reducer, which
      // has to return the same state for the same action however often it runs.
      prepare(name: string, metrics: ScenarioMetrics) {
        return {
          payload: {
            id: nanoid(),
            name,
            savedAt: new Date().toISOString(),
            metrics,
          },
        };
      },
    },
    renameScenario(state, action: PayloadAction<{ id: string; name: string }>) {
      const scenario = state.savedScenarios.find(
        (s) => s.id === action.payload.id
      );
      if (scenario) scenario.name = action.payload.name.trim() || scenario.name;
    },
    deleteScenario(state, action: PayloadAction<string>) {
      state.savedScenarios = state.savedScenarios.filter(
        (s) => s.id !== action.payload
      );
    },
    clearSavedScenarios(state) {
      state.savedScenarios = [];
    },
    // Put the tool back where a saved scenario left it. Anything fetched is
    // dropped only when the restored filters describe a different cohort: the
    // common case is the same merchant under another template, and throwing
    // that response away would blank the figures and refetch what is already
    // held for no gain.
    loadScenario(state, action: PayloadAction<string>) {
      const scenario = state.savedScenarios.find(
        (s) => s.id === action.payload
      );
      if (!scenario) return;
      const sameCohort =
        cohortKey(scenarioInputs(state)) === cohortKey(scenario.inputs);
      applyScenarioInputs(state, scenario.inputs);
      if (sameCohort) return;
      state.apiTpv = null;
      state.apiTxns = null;
      state.apiNet = null;
      state.apiFinalNet = null;
      state.apiReportsMarkup = false;
      state.apiBuckets = null;
      state.apiMonthly = null;
      state.apiRange = null;
      state.apiExcludedCount = null;
      state.areaBusinesses = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchPartnersThunk.pending, (state) => {
        state.partnerSearchStatus = "loading";
      })
      .addCase(searchPartnersThunk.fulfilled, (state, action) => {
        state.partnerSearchStatus = "idle";
        state.partnerSearchResults = action.payload;
      })
      .addCase(searchPartnersThunk.rejected, (state) => {
        state.partnerSearchStatus = "idle";
        state.partnerSearchResults = [];
      })
      .addCase(fetchInsights.pending, (state) => {
        state.fetchStatus = "loading";
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        state.apiTpv = action.payload.tpv;
        state.apiTxns = action.payload.txns;
        state.apiNet = action.payload.net;
        state.apiFinalNet = action.payload.finalNet;
        state.apiReportsMarkup = action.payload.reportsMarkup;
        state.apiBuckets = action.payload.buckets;
        state.apiMonthly = action.payload.monthly;
        state.apiRange = action.payload.range;
        state.areaBusinesses = action.payload.businesses;
        state.apiExcludedCount = action.payload.excludedCount;
      })
      .addCase(fetchInsights.rejected, (state) => {
        state.fetchStatus = "idle";
        // Nothing was fetched, so anything still held is from an earlier
        // selection — keeping it would caption the previous area's volume and
        // markers with the current one.
        state.apiTpv = null;
        state.apiTxns = null;
        state.apiNet = null;
        state.apiBuckets = null;
        state.apiMonthly = null;
        state.apiRange = null;
        state.areaBusinesses = [];
        state.apiExcludedCount = null;
      })

      .addCase(searchBusinessesThunk.pending, (state) => {
        state.bizSearchStatus = "loading";
      })
      .addCase(searchBusinessesThunk.fulfilled, (state, action) => {
        state.bizSearchStatus = "idle";
        state.bizSearchResults = action.payload.options;
        Object.assign(state.bizById, action.payload.stored);
      })
      .addCase(searchBusinessesThunk.rejected, (state) => {
        state.bizSearchStatus = "idle";
      })

      .addCase(searchAreaBusinessesThunk.pending, (state) => {
        state.areaBizSearchStatus = "loading";
      })
      .addCase(searchAreaBusinessesThunk.fulfilled, (state, action) => {
        state.areaBizSearchStatus = "idle";
        state.areaBizSearchResults = action.payload.options;
        Object.assign(state.bizById, action.payload.stored);
      })
      .addCase(searchAreaBusinessesThunk.rejected, (state) => {
        state.areaBizSearchStatus = "idle";
      });
  },
});

export const {
  setChannel,
  setSource,
  setTemplate,
  setCountry,
  selectBiz,
  setAreaCentre,
  selectAreaBiz,
  setAreaAddress,
  setCity,
  setRadiusKm,
  setIndustryGroup,
  setMcc,
  setPartner,
  setCohort,
  setManualTPV,
  setManualTxns,
  setManualTicket,
  setManualVol,
  setMixBasis,
  setMinMonthlyVolume,
  setProposedValue,
  setProposedType,
  setMarkupValue,
  clearMarkupOverrides,
  setNetBasis,
  setSelMonth,
  setPeriod,
  saveScenario,
  renameScenario,
  deleteScenario,
  clearSavedScenarios,
  loadScenario,
} = pricingToolSlice.actions;

export default pricingToolSlice.reducer;

// ── Selectors ──

const sel = (s: RootState) => s.pricingTool;

// Shared with the map so what it frames is always what was queried.
export const selectAreaCentre = createSelector(sel, resolveAreaCentre);

// The period actually in force, with a stale or unset stored one resolved to
// the default. The picker shows this, so what is displayed is what was queried.
export const selectPeriod = createSelector(
  (s: RootState) => s.pricingTool.periodStart,
  (s: RootState) => s.pricingTool.periodEnd,
  (start, end) => resolvePeriod(start, end)
);

// Whole-period monthly averages: server averages (business/area) or manual inputs.
const selectWholeTPV = createSelector(sel, (t) =>
  t.source === "manual" ? t.manualTPV : (t.apiTpv ?? 0)
);

const selectWholeTxns = createSelector(sel, (t) =>
  t.source === "manual" ? t.manualTxns : (t.apiTxns ?? 0)
);

// The server month entry matching the current selection, or null when no month
// is selected (whole period) / the selected month isn't in the current data.
export const selectSelectedMonthEntry = createSelector(
  (s: RootState) => s.pricingTool.apiMonthly,
  (s: RootState) => s.pricingTool.selMonth,
  (s: RootState) => s.pricingTool.source,
  (monthly, selMonth, source) => {
    if (source === "manual" || !monthly || selMonth == null) return null;
    return monthly.find((m) => m.month === selMonth) ?? null;
  }
);

// When a month is selected we use that month's real server metrics; otherwise
// we model the whole fetch window (monthly averages).
export const selectTPV = createSelector(
  selectWholeTPV,
  selectSelectedMonthEntry,
  (whole, entry) => (entry ? entry.avgTpv : whole)
);
export const selectTxns = createSelector(
  selectWholeTxns,
  selectSelectedMonthEntry,
  (whole, entry) => (entry ? entry.avgTransactionCount : whole)
);

// The basis the panel reports on, forced back to "before" for a cohort whose
// response says nothing about markup — so a stale deployment or a direct-only
// cohort can never present a modelled zero cut as a measured one.
// The partner filter only narrows an area cohort. A single business is already
// one merchant, so filtering it by partner could only ever return nothing.
export const selectPartnerFilterActive = createSelector(
  (s: RootState) => s.pricingTool.source,
  (s: RootState) => s.pricingTool.partnerId,
  (source, partnerId) => source === "area" && !!partnerId
);

export const selectMarkupAvailable = createSelector(
  (s: RootState) => s.pricingTool.apiReportsMarkup,
  (s: RootState) => s.pricingTool.source,
  (s: RootState) => s.pricingTool.cohort,
  (s: RootState) => s.pricingTool.markupOverrides,
  (reports, source, cohort, overrides) => {
    // Manual has no measured cut, so the basis waits until one is typed. The
    // direct-cohort switch-off is an area filter and must not hide a single
    // merchant's own contract.
    if (source === "manual") {
      return Object.values(overrides).some(
        (part) => (part.reseller?.value ?? 0) > 0 || (part.isv?.value ?? 0) > 0
      );
    }
    if (!reports) return false;
    if (source === "area" && cohort === "direct") return false;
    return true;
  }
);

export const selectNetBasis = createSelector(
  (s: RootState) => s.pricingTool.netBasis,
  selectMarkupAvailable,
  (basis, available): NetBasis => (available ? basis : "before")
);

// What Klearly actually earned on this volume, or null where nothing was
// measured: manual figures and the industry benchmark are both hypothetical.
export const selectMeasuredNet = createSelector(
  sel,
  selectSelectedMonthEntry,
  selectNetBasis,
  (t, entry, basis) => {
    if (t.source === "manual") return null;
    if (basis === "after") {
      return entry ? entry.avgFinalNetRevenue : t.apiFinalNet;
    }
    return entry ? entry.avgNetRevenue : t.apiNet;
  }
);

export const selectOrder = createSelector(
  (s: RootState) => s.pricingTool.channel,
  (s: RootState) => s.pricingTool.template,
  (ch, tmpl) => getGroupOrder(ch, tmpl)
);

export const selectDefs = createSelector(
  (s: RootState) => s.pricingTool.channel,
  (s: RootState) => s.pricingTool.template,
  (s: RootState) => s.pricingTool.country,
  (ch, tmpl, co) => getActiveDefs(ch, tmpl, co)
);

const selectB2G = createSelector(
  (s: RootState) => s.pricingTool.channel,
  (s: RootState) => s.pricingTool.template,
  (ch, tmpl) => bucketToGroup(ch, tmpl)
);

const selectBucketSharesData = createSelector(
  (s: RootState) => s.pricingTool.industryGroup,
  (s: RootState) => s.pricingTool.channel,
  (grp, ch) => industryShares(grp, ch)
);

// The per-bucket amounts to drive the mix: the selected month's breakdown when
// a month is picked, otherwise the whole-period averages.
const selectActiveBuckets = createSelector(
  (s: RootState) => s.pricingTool.apiBuckets,
  selectSelectedMonthEntry,
  (whole, monthEntry) =>
    monthEntry ? (bucketsFromMonth(monthEntry.buckets) ?? whole) : whole
);

const selectDerivedVolShares = createSelector(
  selectBucketSharesData,
  selectOrder,
  selectB2G,
  selectActiveBuckets,
  (s: RootState) => s.pricingTool.source,
  (industryBucketShares, order, b2g, activeBuckets, source) => {
    // Real per-bucket gross volume from the server (business or area) drives the
    // mix. When a month is selected, activeBuckets is that month's breakdown.
    if (source !== "manual" && activeBuckets) {
      const shares = bucketsToGroupShares(activeBuckets, order, b2g, "gross");
      if (shares) return shares;
    }
    return groupVolShares(industryBucketShares, order, b2g);
  }
);

export const selectActiveVolShares = createSelector(
  selectDerivedVolShares,
  selectOrder,
  (s: RootState) => s.pricingTool.manualVol,
  (s: RootState) => s.pricingTool.mixBasis,
  (derived, order, manualVol, mixBasis) => {
    if (!manualVol) return derived;
    // Count-basis weights describe transactions, so they go through average
    // ticket size before anything is priced on them. Any group left untyped
    // falls back to the measured mix expressed in the same basis.
    const base =
      mixBasis === "count" ? computeTxnShares(derived, order) : derived;
    const w: Record<string, number> = {};
    order.forEach((g) => {
      w[g] = manualVol[g] != null ? manualVol[g] : base[g];
    });
    if (mixBasis === "count") return computeVolShares(w, order);
    let sum = 0;
    order.forEach((g) => (sum += w[g]));
    if (sum <= 0) sum = 1;
    const v: Record<string, number> = {};
    order.forEach((g) => (v[g] = (w[g] / sum) * 100));
    return v;
  }
);

export const selectTxnShares = createSelector(
  selectActiveVolShares,
  selectOrder,
  selectB2G,
  selectActiveBuckets,
  (s: RootState) => s.pricingTool.source,
  (s: RootState) => s.pricingTool.manualVol,
  (volShares, order, b2g, activeBuckets, source, manualVol) => {
    // Use the server's real per-bucket transaction counts when available (and the
    // user hasn't manually overridden the mix); otherwise estimate from volume.
    if (!manualVol && source !== "manual" && activeBuckets) {
      const shares = bucketsToGroupShares(activeBuckets, order, b2g, "txns");
      if (shares) return shares;
    }
    return computeTxnShares(volShares, order);
  }
);

// The weights as typed, filled in from the measured mix wherever nothing was,
// in whichever basis is selected. This is what the input boxes show, so a box
// holds the number the user put in it and does not move when a neighbour is
// edited. They need not total 100; selectActiveVolShares normalises on read.
export const selectMixWeights = createSelector(
  selectDerivedVolShares,
  selectTxnShares,
  selectOrder,
  (s: RootState) => s.pricingTool.manualVol,
  (s: RootState) => s.pricingTool.mixBasis,
  (derivedVol, txnShares, order, manualVol, mixBasis) => {
    const measured = mixBasis === "count" ? txnShares : derivedVol;
    const out: Record<string, number> = {};
    order.forEach((g) => (out[g] = manualVol?.[g] ?? measured[g] ?? 0));
    return out;
  }
);

// Whether the typed weights add up to 100. A reader who typed 40/40/40 should
// be told the shares on screen are scaled, rather than left wondering why none
// of them match what they typed.
export const selectMixWeightTotal = createSelector(
  selectMixWeights,
  selectOrder,
  (w, order) => order.reduce((a, g) => a + (w[g] || 0), 0)
);

// What those merchants are actually charged and what their volume actually
// costs, derived from the response the current filters returned. Null for manual
// volume (nothing was measured) and for a response with no revenue to divide.
//
// It hangs off `selectActiveBuckets`, which is the selected month's breakdown
// when a month is picked and the whole-period averages otherwise — so the prices
// restate themselves on any change of area, industry, radius, period or month,
// without a reducer having to remember to.
export const selectMeasuredPricing = createSelector(
  selectActiveBuckets,
  selectOrder,
  selectDefs,
  selectB2G,
  selectTPV,
  selectTxns,
  (s: RootState) => s.pricingTool.source,
  (buckets, order, defs, b2g, tpv, txns, source) =>
    source === "manual" || !buckets
      ? null
      : deriveGroupPricing(buckets, order, defs, b2g, tpv, txns)
);

// The prices on screen: what was measured, with anything typed winning over it,
// falling back to the list price where neither applies.
export const selectPrices = createSelector(
  selectMeasuredPricing,
  selectOrder,
  selectDefs,
  (s: RootState) => s.pricingTool.priceOverrides,
  (measured, order, defs, overrides) => {
    const out: Record<string, PriceSpec> = {};
    order.forEach((g) => {
      out[g] = overrides[g] ?? measured?.prices[g] ?? defs[g].current;
    });
    return out;
  }
);

// The same, untouched by anything typed: today, for comparing a proposal to.
export const selectMeasuredPrices = createSelector(
  selectMeasuredPricing,
  selectOrder,
  selectDefs,
  (measured, order, defs) => {
    const out: Record<string, PriceSpec> = {};
    order.forEach((g) => (out[g] = measured?.prices[g] ?? defs[g].current));
    return out;
  }
);

// What each partner takes out of each group, with anything retyped winning over
// the measured cut. Null for a group of a cohort with nothing measured, which is
// what hides the breakdown rather than showing it as zero.
export const selectMarkups = createSelector(
  selectMeasuredPricing,
  selectOrder,
  selectDefs,
  (s: RootState) => s.pricingTool.markupOverrides,
  (s: RootState) => s.pricingTool.source,
  (measured, order, defs, overrides, source) => {
    const out: Record<string, GroupMarkup | null> = {};
    order.forEach((g) => {
      const o = overrides[g];
      // Manual volume was typed, so there is no measured cut to start from.
      // Zeros keep the row editable; the basis stays off until one is non-zero.
      if (source === "manual") {
        const valueType = defs[g].current.valueType;
        const zero = { value: 0, valueType };
        out[g] = {
          reseller: o?.reseller ?? zero,
          isv: o?.isv ?? zero,
        };
        return;
      }
      const m = measured?.markups[g];
      if (!m) {
        out[g] = null;
        return;
      }
      out[g] = { reseller: o?.reseller ?? m.reseller, isv: o?.isv ?? m.isv };
    });
    return out;
  }
);

// What a group's cut costs in total, in the group's own unit. Only the parts
// stated in that unit can be added to it — a per-transaction cut and a
// percentage one do not sum — which is also why the server reports both.
function markupTotal(
  markup: GroupMarkup | null,
  valueType: PriceSpec["valueType"]
): number {
  if (!markup) return 0;
  return [markup.reseller, markup.isv].reduce(
    (sum, part) => sum + (part.valueType === valueType ? part.value : 0),
    0
  );
}

// What each group's volume costs Klearly. On the after-markup basis the
// partners' cut is added to it, which is all it takes for every figure
// downstream — the rows, the totals, the effective rate — to become
// after-markup, because each of them is already price less cost.
export const selectCosts = createSelector(
  selectMeasuredPricing,
  selectOrder,
  selectDefs,
  selectNetBasis,
  selectMarkups,
  (measured, order, defs, basis, markups) => {
    const out: Record<string, PriceSpec> = {};
    order.forEach((g) => {
      const cost = measured?.costs[g] ?? defs[g].cost;
      out[g] =
        basis === "after"
          ? {
              ...cost,
              value: cost.value + markupTotal(markups[g], cost.valueType),
            }
          : cost;
    });
    return out;
  }
);

// The floor a price has to clear to earn Klearly anything: processing cost plus
// whatever the partners take. Stated in its own right rather than read off
// `selectCosts`, which carries the cut on one basis and not the other — and a
// price above cost but below cost-plus-markup earns the partners and nobody
// else, which is the mistake worth catching whichever basis is on screen.
export const selectPriceFloors = createSelector(
  selectMeasuredPricing,
  selectOrder,
  selectDefs,
  selectMarkups,
  (measured, order, defs, markups) => {
    const out: Record<
      string,
      { cost: PriceSpec; withMarkup: number; hasMarkup: boolean }
    > = {};
    order.forEach((g) => {
      const cost = measured?.costs[g] ?? defs[g].cost;
      const cut = markupTotal(markups[g], cost.valueType);
      out[g] = { cost, withMarkup: cost.value + cut, hasMarkup: cut > 0 };
    });
    return out;
  }
);

// Klearly's own share of each group's price: the total the merchant pays, less
// both partners' cuts. Derived rather than stored, so the figures a row shows
// can never disagree about what the merchant is charged.
export const selectKlearlyTake = createSelector(
  selectPrices,
  selectOrder,
  selectMarkups,
  (prices, order, markups) => {
    const out: Record<string, PriceSpec> = {};
    order.forEach((g) => {
      const price = prices[g];
      out[g] = {
        ...price,
        value: price.value - markupTotal(markups[g], price.valueType),
      };
    });
    return out;
  }
);

// Whether any group's price is someone's edit rather than the measured figure,
// so the panel can say a proposal is on screen rather than a statement of today.
export const selectHasPriceOverrides = createSelector(
  selectOrder,
  (s: RootState) => s.pricingTool.priceOverrides,
  (order, overrides) => order.some((g) => overrides[g] != null)
);

export const selectScenario = createSelector(
  selectPrices,
  selectTPV,
  selectTxns,
  selectOrder,
  selectDefs,
  selectActiveVolShares,
  selectTxnShares,
  selectCosts,
  (prices, tpv, txns, order, defs, volShares, txnShares, costs) =>
    computeScenario(prices, tpv, txns, order, defs, volShares, txnShares, costs)
);

export const selectEffectiveRate = createSelector(
  selectScenario,
  selectTPV,
  ({ totalNet }, tpv) => (tpv > 0 ? (totalNet / tpv) * 100 : 0)
);

// Whole-period scenario: current pricing applied to the average month.
const selectWholePeriodScenario = createSelector(
  selectPrices,
  selectWholeTPV,
  selectWholeTxns,
  selectOrder,
  selectDefs,
  selectActiveVolShares,
  selectTxnShares,
  selectCosts,
  (prices, tpv, txns, order, defs, volShares, txnShares, costs) =>
    computeScenario(prices, tpv, txns, order, defs, volShares, txnShares, costs)
);

// Annual net take:
//  • A month is selected → that month annualized (monthly net × 12).
//  • Whole period (no month) → each real month's net take, scaled from the
//    period to a year (current pricing applied to each month's actual volume,
//    transactions & mix).
//  • No per-month history (manual / area-benchmark) → average month × 12.
// In every case a single month's net can never exceed the annual total.
// A month still in progress has already been projected to a full-month rate by
// the time it gets here, so it needs no special handling and counts as one.
export const selectAnnualBreakdown = createSelector(
  selectPrices,
  (s: RootState) => s.pricingTool.apiMonthly,
  selectOrder,
  selectDefs,
  selectB2G,
  selectActiveVolShares,
  selectTxnShares,
  (s: RootState) => s.pricingTool.manualVol,
  selectWholePeriodScenario,
  selectWholeTPV,
  selectSelectedMonthEntry,
  selectScenario,
  selectCosts,
  (
    proposed,
    monthly,
    order,
    defs,
    b2g,
    volShares,
    txnShares,
    manualVol,
    wholeScenario,
    wholeTpv,
    monthEntry,
    scenario,
    costs
  ) => {
    // A specific month is selected: annualize it (project that month's real
    // volume, transactions & mix across a full year) so the annual figure tracks
    // the selection. monthly net × 12 keeps the monthly ≤ annual invariant.
    if (monthEntry) {
      const avgMonthlyNet = scenario.totalNet;
      return {
        annual: avgMonthlyNet * 12,
        monthCount: 12,
        perMonth: false,
        avgMonthlyNet,
        selMonth: monthEntry.month,
      };
    }
    if (monthly && monthly.length > 0) {
      const monthsNet = monthly.map((m) => {
        // Prefer each month's own real mix; fall back to the active mix when the
        // month has no per-bucket data or the user manually overrode the mix.
        let vs = volShares;
        let ts = txnShares;
        if (!manualVol && m.buckets) {
          const mb = bucketsFromMonth(m.buckets);
          if (mb) {
            vs = bucketsToGroupShares(mb, order, b2g, "gross") ?? vs;
            ts = bucketsToGroupShares(mb, order, b2g, "txns") ?? ts;
          }
        }
        return computeScenario(
          proposed,
          m.avgTpv,
          m.avgTransactionCount,
          order,
          defs,
          vs,
          ts,
          // The measured costs every other figure on the panel is netted
          // against, and on the after-markup basis the partner cut with them.
          // Leaving them out priced each month against the list cost instead,
          // which is why the annual was not twelve times the month.
          costs
        ).totalNet;
      });
      const periodNet = monthsNet.reduce((a, b) => a + b, 0);
      const monthCount = monthly.length;
      // Scale the period up to a year. For the default twelve months this is
      // the sum unchanged; for a shorter period the sum is that period's net,
      // not a year's, and reporting it as annual would understate it. The
      // factor is never below 1, so a single month still cannot exceed the
      // annual total.
      const annual = monthCount > 0 ? periodNet * (12 / monthCount) : 0;
      return {
        annual,
        monthCount,
        perMonth: true,
        avgMonthlyNet: monthCount > 0 ? periodNet / monthCount : 0,
        selMonth: null,
        projected: monthly.some((m) => m.projected),
      };
    }
    const annual = wholeTpv > 0 ? wholeScenario.totalNet * 12 : 0;
    return {
      annual,
      monthCount: 12,
      perMonth: false,
      avgMonthlyNet: wholeTpv > 0 ? wholeScenario.totalNet : 0,
      selMonth: null,
    };
  }
);

export const selectAnnualNet = createSelector(
  selectAnnualBreakdown,
  (b) => b.annual
);

export const selectBreakdownItems = createSelector(
  selectScenario,
  selectB2G,
  ({ rows }, b2g) => {
    const total = rows.reduce(
      (acc: number, r: any) => acc + Math.max(0, r.net),
      0
    );
    return rows.map((r: any, i: number) => ({
      label: (r.def?.label || r.g) as string,
      net: r.net as number,
      pct: total > 0 ? (Math.max(0, r.net) / total) * 100 : 0,
      color: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length],
      buckets: groupBucketLabels(b2g, r.g as string),
    }));
  }
);

export const selectGroupBuckets = createSelector(
  selectOrder,
  selectB2G,
  (order, b2g) => {
    const out: Record<string, string[]> = {};
    order.forEach((g) => (out[g] = groupBucketLabels(b2g, g)));
    return out;
  }
);

export const selectBaselineScenario = createSelector(
  selectOrder,
  selectDefs,
  selectTPV,
  selectTxns,
  selectActiveVolShares,
  selectTxnShares,
  selectMeasuredPrices,
  selectCosts,
  (order, defs, tpv, txns, volShares, txnShares, prices, costs) =>
    computeScenario(prices, tpv, txns, order, defs, volShares, txnShares, costs)
);

// What the proposal is compared against: the net Klearly actually earned over
// the period, falling back to the modelled current list prices when nothing was
// measured (manual volume, industry benchmark).
//
// The list-price model is the wrong answer here whenever a real figure exists.
// It assumes every merchant sits on the standard card, so it read 0.4004% for
// an area that in fact returned 0.3748% — a "before" 7% flatter than reality,
// which understates every proposal drawn against it by the same margin.
// What the merchant pays, before any of it is spent. The net take answers what
// Klearly earns; this answers what it costs the merchant to accept cards, which
// is the number they argue about and the one a quote is built from.
export const selectMerchantFee = createSelector(
  selectScenario,
  ({ totalRev }) => totalRev
);

// Processing cost with no partner cut in it. On the after-markup basis the
// scenario's own cost includes that cut, and adding it here would count the
// partners twice when the bill is checked against net take.
const selectProcessingCosts = createSelector(
  selectMeasuredPricing,
  selectOrder,
  selectDefs,
  (measured, order, defs) => {
    const out: Record<string, PriceSpec> = {};
    order.forEach((g) => {
      out[g] = measured?.costs[g] ?? defs[g].cost;
    });
    return out;
  }
);

export const selectProcessingFees = createSelector(
  selectPrices,
  selectTPV,
  selectTxns,
  selectOrder,
  selectDefs,
  selectActiveVolShares,
  selectTxnShares,
  selectProcessingCosts,
  (prices, tpv, txns, order, defs, volShares, txnShares, costs) =>
    computeScenario(prices, tpv, txns, order, defs, volShares, txnShares, costs)
      .totalCost
);

// The same fee at today's measured prices, so a proposal can be read as a rise
// or a cut rather than as a figure on its own.
export const selectBaselineMerchantFee = createSelector(
  selectBaselineScenario,
  ({ totalRev }) => totalRev
);

export const selectBaselineNet = createSelector(
  selectMeasuredNet,
  selectBaselineScenario,
  (measured, { totalNet }) => measured ?? totalNet
);

// Whether the baseline above is measured rather than modelled, so the panel can
// say which of the two it is showing.
export const selectBaselineIsMeasured = createSelector(
  selectMeasuredNet,
  (measured) => measured != null
);

export const selectBaseEffectiveRate = createSelector(
  selectBaselineNet,
  selectTPV,
  (baseNet, tpv) => (tpv > 0 ? (baseNet / tpv) * 100 : 0)
);

// Whether the panel is stating today rather than a proposal: no price has been
// typed, and there is a measured figure for it to state.
export const selectStatingToday = createSelector(
  selectHasPriceOverrides,
  selectBaselineIsMeasured,
  (hasProposal, measured) => !hasProposal && measured
);

// The monthly net on screen — measured until a price is typed, modelled after —
// and the rate it comes to. Both are selectors rather than a panel's own
// arithmetic so that a scenario saved off the screen holds the figure that was
// on it.
export const selectShownNet = createSelector(
  selectStatingToday,
  selectBaselineNet,
  selectScenario,
  (statingToday, baseNet, { totalNet }) => (statingToday ? baseNet : totalNet)
);

export const selectShownEffectiveRate = createSelector(
  selectShownNet,
  selectTPV,
  (net, tpv) => (tpv > 0 ? (net / tpv) * 100 : 0)
);

// ── Saved scenarios ──

export const selectSavedScenarios = (s: RootState) =>
  s.pricingTool.savedScenarios;

// The figures a save would keep.
export const selectCurrentScenarioMetrics = createSelector(
  selectTPV,
  selectTxns,
  selectShownNet,
  selectAnnualNet,
  (tpv, txns, monthlyNet, annualNet): ScenarioMetrics => ({
    tpv,
    txns,
    monthlyNet,
    annualNet,
  })
);

// What the live figures are modelled on, for deciding whether a saved
// scenario's are a baseline for them or merely a different question.
export const selectVolumeBasisKey = createSelector(sel, (t) =>
  volumeBasisKey(scenarioInputs(t))
);
